import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { fmtNum, formatDate, STATUS_COLORS } from "@/lib/constants";
import PasswordInput from "@/components/PasswordInput";

const empty = {
  title: "",
  short_name: "",
  contact_person: "",
  phone: "",
  email: "",
  tax_office: "",
  tax_number: "",
  city: "",
  district: "",
  address: "",
  main_delivery_address: "",
  extra_delivery_addresses: [],
  note: "",
  active: true,
  username: "",
  password: "",
};

export default function CompanyForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const [form, setForm] = useState(empty);
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (id) {
      api.get(`/admin/companies/${id}`).then(({ data }) => {
        setForm({ ...empty, ...data, password: "" });
        setStats(data.stats);
        setOrders(data.orders || []);
      });
    }
  }, [id]);

  const set = (k, v) => setForm((d) => ({ ...d, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.short_name || !form.contact_person || !form.phone || !form.main_delivery_address) {
      toast.error("Zorunlu alanları doldurun");
      return;
    }
    if (!id && (!form.username || !form.password)) {
      toast.error("Kullanıcı adı ve şifre zorunlu");
      return;
    }
    if (id && !form.username) {
      toast.error("Kullanıcı adı boş olamaz");
      return;
    }
    setSaving(true);
    try {
      if (id) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await api.put(`/admin/companies/${id}`, payload);
        toast.success("Güncellendi");
      } else {
        await api.post("/admin/companies", form);
        toast.success("Firma oluşturuldu");
      }
      nav("/admin/firmalar");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Hata");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="admin-company-form">
      <Link to="/admin/firmalar" className="inline-flex items-center gap-2 text-sm text-[#6b655c] hover:text-[#c8a97e]">
        <ArrowLeft size={14}/> Firmalara dön
      </Link>
      <div>
        <span className="label-tag">{id ? "Düzenle" : "Yeni"}</span>
        <h1 className="font-serif text-4xl mt-2">{id ? form.short_name : "Yeni Firma"}</h1>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Mini label="Toplam Sipariş" value={stats.total_orders} />
          <Mini label="Toplam Uzunluk" value={`${fmtNum((stats.total_meters || 0) * 100)} cm`} />
          <Mini label="Toplam m²" value={`${fmtNum(stats.total_sqm)} m²`} />
          <Mini label="Son Sipariş" value={stats.last_order ? formatDate(stats.last_order) : "—"} />
        </div>
      )}

      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-[#eae5dc] p-6 space-y-4">
          <h3 className="font-serif text-xl">Firma Bilgileri</h3>
          <Field label="Firma Unvanı *">
            <Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="company-form-title" />
          </Field>
          <Field label="Kısa Ad *">
            <Input value={form.short_name} onChange={(e) => set("short_name", e.target.value)} data-testid="company-form-shortname" />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Yetkili Kişi *">
              <Input value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} data-testid="company-form-contact" />
            </Field>
            <Field label="Telefon *">
              <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="company-form-phone" />
            </Field>
          </div>
          <Field label="E-posta">
            <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Vergi Dairesi"><Input value={form.tax_office} onChange={(e) => set("tax_office", e.target.value)} /></Field>
            <Field label="Vergi Numarası"><Input value={form.tax_number} onChange={(e) => set("tax_number", e.target.value)} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="İl"><Input value={form.city} onChange={(e) => set("city", e.target.value)} /></Field>
            <Field label="İlçe"><Input value={form.district} onChange={(e) => set("district", e.target.value)} /></Field>
          </div>
          <Field label="Açık Adres"><Textarea rows={2} value={form.address} onChange={(e) => set("address", e.target.value)} /></Field>
          <Field label="Ana Teslimat Adresi *">
            <Textarea rows={2} value={form.main_delivery_address} onChange={(e) => set("main_delivery_address", e.target.value)} data-testid="company-form-delivery" />
          </Field>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs uppercase tracking-wider text-[#6b655c]">Ek Teslimat Adresleri</label>
              <Button type="button" variant="outline" size="sm" onClick={() => set("extra_delivery_addresses", [...form.extra_delivery_addresses, { label: "", address: "" }])}>
                <Plus size={12}/> Ekle
              </Button>
            </div>
            {form.extra_delivery_addresses.map((a, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 mb-2">
                <Input className="col-span-3" placeholder="Etiket" value={a.label} onChange={(e) => set("extra_delivery_addresses", form.extra_delivery_addresses.map((x, idx) => idx === i ? { ...x, label: e.target.value } : x))} />
                <Input className="col-span-8" placeholder="Adres" value={a.address} onChange={(e) => set("extra_delivery_addresses", form.extra_delivery_addresses.map((x, idx) => idx === i ? { ...x, address: e.target.value } : x))} />
                <button type="button" onClick={() => set("extra_delivery_addresses", form.extra_delivery_addresses.filter((_, idx) => idx !== i))} className="col-span-1 hover:bg-red-50 text-red-600 flex items-center justify-center"><X size={14}/></button>
              </div>
            ))}
          </div>

          <Field label="Firma Notu"><Textarea rows={2} value={form.note} onChange={(e) => set("note", e.target.value)} /></Field>
        </div>

        <div className="space-y-6">
          <div className="bg-white border border-[#eae5dc] p-6 space-y-4">
            <h3 className="font-serif text-xl">Hesap & Erişim</h3>
            <Field label="Kullanıcı Adı *">
              <Input value={form.username} onChange={(e) => set("username", e.target.value)} data-testid="company-form-username" />
              {id && <p className="text-xs text-[#6b655c] mt-1">Bayinin bu kullanıcı adı ile giriş yapacağını unutmayın.</p>}
            </Field>
            <Field label={id ? "Yeni Şifre (boş bırak: değiştirme)" : "Şifre *"}>
              <PasswordInput value={form.password} onChange={(e) => set("password", e.target.value)} testid="company-form-password" />
            </Field>
            <div className="flex items-center justify-between pt-2">
              <span className="text-sm">Aktif</span>
              <Switch checked={form.active} onCheckedChange={(v) => set("active", v)} />
            </div>
          </div>

          {orders.length > 0 && (
            <div className="bg-white border border-[#eae5dc] p-6">
              <h3 className="font-serif text-lg mb-3">Son Siparişler</h3>
              <div className="space-y-2">
                {orders.slice(0, 8).map((o) => (
                  <Link key={o.id} to={`/admin/siparisler/${o.id}`} className="block text-sm border-b border-[#f0ebe2] pb-2 hover:text-[#c8a97e]">
                    <div className="flex justify-between">
                      <span className="font-medium">{o.order_no}</span>
                      <span className={`text-[0.65rem] px-2 py-0.5 ${STATUS_COLORS[o.status]}`}>{o.status}</span>
                    </div>
                    <div className="text-xs text-[#6b655c]">{formatDate(o.created_at)} · {fmtNum(o.total_sqm)} m²</div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="lg:col-span-3 flex gap-3">
          <Button type="submit" disabled={saving} className="btn-luxe" data-testid="company-form-save-btn">{saving ? "Kaydediliyor…" : id ? "Güncelle" : "Oluştur"}</Button>
          <Link to="/admin/firmalar" className="btn-luxe-outline text-xs">Vazgeç</Link>
        </div>
      </form>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs uppercase tracking-wider text-[#6b655c] block mb-1.5">{label}</label>
    {children}
  </div>
);

const Mini = ({ label, value }) => (
  <div className="bg-white border border-[#eae5dc] p-4">
    <div className="text-xs uppercase tracking-wider text-[#6b655c]">{label}</div>
    <div className="font-serif text-2xl mt-1">{value}</div>
  </div>
);
